import styled from "@emotion/styled";
import { LocationDescriptorObject } from "history";
import React from "react";
import { NavLink } from "react-router-dom";
import { Icon, SemanticCOLORS, SemanticICONS } from "semantic-ui-react";
import { Box } from "../../ui/_box";

const StyledNavLink = styled(NavLink)`
  color: var(--gray-100);
  :hover {
    color: white;
    background-color: var(--gray-800);
  }
  &.active {
    color: white;
    background-color: var(--gray-900);
  }
`;
const StyledIcon = styled(Icon)`
  &&& {
    margin-bottom: 4px;
    margin-right: 12px;
  }
`;
export const SidebarLink = ({
  to,
  children,
  show = true,
  count,
  icon,
  iconColor,
  wrapLabel = false,
}: {
  show?: boolean;
  to: LocationDescriptorObject<unknown> | string;
  children: React.ReactNode;
  count?: string;
  icon?: SemanticICONS;
  iconColor?: SemanticCOLORS;
  // Opt-in only: SidebarLink is shared by every sidebar in the product,
  // including entries that rely on the default rendering staying exactly as
  // it was (e.g. DeploymentSidebar's "Notifications" `count` badge, which
  // must sit immediately after its short label, not pushed to the row's
  // right edge). Wrapping `children` in a flex="1" box fixes a long label's
  // alignment once it wraps onto a second line (see "Credits & Usage
  // (deprecated)" in OrganizationSidebar), but changes how ANY sibling after
  // it - like `count` - gets laid out. So it is opt-in per entry: every other
  // consumer renders exactly as it did before this prop existed.
  wrapLabel?: boolean;
}) => {
  if (!show) {
    return null;
  }
  if (wrapLabel) {
    return (
      <HashNavLink to={to}>
        {/* minHeight (not height) lets the row grow when the label wraps
            onto a second line, and the label gets its own flex="1" box
            instead of being a bare text node - otherwise it can end up
            looking centered instead of left-aligned once wrapped. */}
        <Box padding="0 16px" width="100%" cursor="pointer" minHeight="36px" display="flex" align="center">
          {icon && <StyledIcon inverted name={icon} color={iconColor} />}
          <Box flex="1" textAlign="left">
            {children}
          </Box>
          {count && (
            <Box
              height="20px"
              width="20px"
              borderRadius="50%"
              display="flex"
              align="center"
              justify="center"
              backgroundColor="var(--red-700)"
              color="var(--gray-100)"
              fontSize="10px"
              fontWeight="700"
              margin="0 0 0 8px"
            >
              {count}
            </Box>
          )}
        </Box>
      </HashNavLink>
    );
  }
  return (
    <HashNavLink to={to}>
      <Box padding="0 16px" width="100%" cursor="pointer" height="36px" display="flex" align="center">
        {icon && <StyledIcon inverted name={icon} color={iconColor} />}
        {children}
        {count && (
          <Box
            height="20px"
            width="20px"
            borderRadius="50%"
            display="flex"
            align="center"
            justify="center"
            backgroundColor="var(--red-700)"
            color="var(--gray-100)"
            fontSize="10px"
            fontWeight="700"
            margin="0 0 0 8px"
          >
            {count}
          </Box>
        )}
      </Box>
    </HashNavLink>
  );
};
const HashNavLink = ({ to, children }: { to: LocationDescriptorObject<unknown> | string; children: React.ReactNode }) => {
  return (
    <StyledNavLink
      to={to}
      exact
      isActive={(_match, location) => {
        if (typeof to === "string" && location.pathname === to) {
          return true;
        }
        if (typeof to === "object") {
          if (location.pathname === to.pathname) {
            if (location.hash === to.hash) {
              return true;
            }
            if (!to.hash && !location.hash) {
              return true;
            }
          }
        }
        return false;
      }}
    >
      {children}
    </StyledNavLink>
  );
};
